import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  FiPackage, FiDownload, FiPrinter, FiSearch, FiCheckCircle, FiClock, FiFileText, FiExternalLink
} from 'react-icons/fi';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { colors } from '../../theme/colors';
import PageLayout from '../../components/PageLayout';
import { formatNumber, formatCurrency } from '../../utils/formatters';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { useNavigate } from 'react-router-dom';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const STATUS_COLORS = {
  Draft: colors.neutral[500],
  Approved: colors.info.main,
  Received: colors.warning.main,
  Invoiced: colors.success.main,
  Cancelled: colors.error.main
};

const GRNTracking = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + loaded rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('grn-tracking');
  const navigate = useNavigate();

  const [grns, setGRNs] = useState(() => savedMem?.grns ?? []);
  const [purchaseInvoices, setPurchaseInvoices] = useState(() => savedMem?.purchaseInvoices ?? []);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(() => savedMem ? false : true);

  const [fromDate, setFromDate] = useState(() => savedMem?.fromDate ?? '');
  const [toDate, setToDate] = useState(() => savedMem?.toDate ?? '');
  const [selectedVendor, setSelectedVendor] = useState(() => savedMem?.selectedVendor ?? 'all');
  const [selectedStatus, setSelectedStatus] = useState(() => savedMem?.selectedStatus ?? 'all'); // all | pending | invoiced

  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const snap = await getDocs(query(ref, orderBy('vendorName')));
      setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('vendors', e); }
  }, [activeCompany]);

  const loadData = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');
      const conditions = [where('tranTypeCode', '==', 'GRN')];
      if (activeFinancialYear?.id) conditions.push(where('financialYearId', '==', activeFinancialYear.id));
      const grnSnap = await getDocs(query(ref, ...conditions));
      let allGRNs = grnSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => (!activeBranch || activeBranch.id === 'all') ? true : t.branchId === activeBranch.id);
      allGRNs.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setGRNs(allGRNs);

      // Load all PI/NPI docs to find which GRNs they reference
      const piConditions = [where('tranTypeCode', 'in', ['PI', 'NPI'])];
      if (activeFinancialYear?.id) piConditions.push(where('financialYearId', '==', activeFinancialYear.id));
      const piSnap = await getDocs(query(ref, ...piConditions));
      setPurchaseInvoices(piSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading GRN tracking data:', e);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeFinancialYear, activeBranch]);

  useEffect(() => { loadVendors(); }, [loadVendors]);
  // Backward memory: on Back (snapshot present) skip the initial auto-load so we
  // reuse the restored rows with 0 re-reads; a later Refresh still re-fetches.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    loadData();
  }, [loadData]);

  // Backward memory: keep the snapshot in sync (filters are display-only over the
  // already-loaded rows), so Back restores this exact view with 0 re-reads.
  useEffect(() => {
    if (loading) return;
    saveMemory({ grns, purchaseInvoices, fromDate, toDate, selectedVendor, selectedStatus });
  }, [loading, grns, purchaseInvoices, fromDate, toDate, selectedVendor, selectedStatus, saveMemory]);

  // Build a map: grnId -> [linked PIs]
  const pisByGRN = useMemo(() => {
    const map = {};
    purchaseInvoices.forEach(pi => {
      if (pi.parentTranType !== 'GRN') return;
      if (pi.status === 'Cancelled') return;
      const ids = (Array.isArray(pi.parentIds) && pi.parentIds.length > 0)
        ? pi.parentIds
        : (pi.parentId ? [pi.parentId] : []);
      ids.forEach(gid => {
        if (!map[gid]) map[gid] = [];
        map[gid].push(pi);
      });
    });
    return map;
  }, [purchaseInvoices]);

  // Enrich GRNs with invoicing status
  const enriched = useMemo(() => {
    return grns.map(grn => {
      const linkedPIs = pisByGRN[grn.id] || [];
      const isInvoiced = linkedPIs.length > 0 || grn.status === 'Invoiced';
      const totalAmount = (grn.lines || []).reduce((s, l) => s + (Number(l.lineTotal) || (Number(l.quantity || 0) * Number(l.unitCost || l.unitPrice || 0))), 0);
      const totalQty = (grn.lines || []).reduce((s, l) => s + (Number(l.acceptedQty || l.receivedQty || l.quantity) || 0), 0);
      return { ...grn, linkedPIs, isInvoiced, totalAmount, totalQty };
    });
  }, [grns, pisByGRN]);

  // Apply filters
  const filtered = useMemo(() => {
    return enriched.filter(g => {
      if (selectedVendor !== 'all' && g.partyId !== selectedVendor) return false;
      if (fromDate && (g.docDate || '') < fromDate) return false;
      if (toDate && (g.docDate || '') > toDate) return false;
      if (selectedStatus === 'pending' && g.isInvoiced) return false;
      if (selectedStatus === 'invoiced' && !g.isInvoiced) return false;
      return true;
    });
  }, [enriched, selectedVendor, selectedStatus, fromDate, toDate]);

  const summary = useMemo(() => ({
    total: filtered.length,
    pending: filtered.filter(g => !g.isInvoiced).length,
    invoiced: filtered.filter(g => g.isInvoiced).length,
    pendingValue: filtered.filter(g => !g.isInvoiced).reduce((s, g) => s + (g.totalAmount || 0), 0),
    invoicedValue: filtered.filter(g => g.isInvoiced).reduce((s, g) => s + (g.totalAmount || 0), 0)
  }), [filtered]);

  const handleExport = () => {
    if (filtered.length === 0) return;
    let csv = 'GRN Tracking Report\n';
    csv += `Company,${activeCompany?.companyName || ''}\n`;
    if (fromDate || toDate) csv += `Date Range,${fromDate || 'Start'} to ${toDate || 'End'}\n`;
    csv += `Generated,${new Date().toLocaleString()}\n\n`;
    csv += 'GRN #,Date,Vendor,Status,Total Qty,Total Amount,Linked PI(s)\n';
    filtered.forEach(g => {
      const piNums = g.linkedPIs.map(p => p.docNumber).join('; ');
      csv += `"${g.docNumber || ''}","${g.docDate || ''}","${(g.partyName || '').replace(/"/g, '""')}","${g.isInvoiced ? 'Invoiced' : 'Pending'}",${g.totalQty || 0},${g.totalAmount || 0},"${piNums}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GRN_Tracking_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    const rangeLine = (fromDate || toDate) ? ` | Date Range: ${fromDate || 'Start'} to ${toDate || 'End'}` : '';
    w.document.write(`<html><head><title>GRN Tracking Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary{display:flex;gap:14px;margin-bottom:18px;flex-wrap:wrap}
        .box{padding:10px 16px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020}
        .box .lbl{font-size:10px;color:#666;text-transform:uppercase}
        .box .val{font-size:15px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        .right{text-align:right}
        .ok{color:#28A745;font-weight:600}
        .pending{color:#FFC107;font-weight:600}
      </style></head><body>
      <h1>GRN Tracking Report</h1>
      <div class="meta">${activeCompany?.companyName || ''}${rangeLine} | Generated: ${new Date().toLocaleString()}</div>
      <div class="summary">
        <div class="box"><div class="lbl">Total GRNs</div><div class="val">${summary.total}</div></div>
        <div class="box"><div class="lbl">Pending Invoice</div><div class="val">${summary.pending}</div></div>
        <div class="box"><div class="lbl">Invoiced</div><div class="val">${summary.invoiced}</div></div>
        <div class="box"><div class="lbl">Pending Value</div><div class="val">${formatCurrency(summary.pendingValue)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>GRN #</th><th>Date</th><th>Vendor</th><th>Status</th>
          <th class="right">Qty</th><th class="right">Amount</th><th>Linked PI(s)</th>
        </tr></thead>
        <tbody>
          ${filtered.map(g => `<tr>
            <td><strong>${g.docNumber || ''}</strong></td>
            <td>${g.docDate || ''}</td>
            <td>${g.partyName || ''}</td>
            <td class="${g.isInvoiced ? 'ok' : 'pending'}">${g.isInvoiced ? 'Invoiced' : 'Pending'}</td>
            <td class="right">${formatNumber(g.totalQty || 0)}</td>
            <td class="right">${formatCurrency(g.totalAmount || 0)}</td>
            <td>${g.linkedPIs.map(p => p.docNumber).join(', ') || '-'}</td>
          </tr>`).join('')}
        </tbody>
      </table>
      </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const handleOpenGRN = (g) => navigateToTransaction(navigate, 'GRN', g.id);
  const handleOpenPI = (pi) => navigateToTransaction(navigate, pi.tranTypeCode, pi.id);

  return (
    <PageLayout pageTitle="GRN Tracking" breadcrumb="Reports / Purchase Reports / GRN Tracking">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>From Date</Label>
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Vendor</Label>
            <Select value={selectedVendor} onChange={e => setSelectedVendor(e.target.value)}>
              <option value="all">All Vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.vendorName || v.companyName}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Invoicing Status</Label>
            <Select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="pending">Pending Invoice</option>
              <option value="invoiced">Invoiced</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>
        <ButtonGroup>
          <Button onClick={handleExport} disabled={filtered.length === 0}><FiDownload /> Export CSV</Button>
          <Button onClick={handlePrint} disabled={filtered.length === 0}><FiPrinter /> Print</Button>
          <Button $primary onClick={loadData} disabled={loading}><FiSearch /> {loading ? 'Loading...' : 'Refresh'}</Button>
        </ButtonGroup>
      </FiltersCard>

      <SummaryCards>
        <SummaryCard>
          <SummaryIcon $color={colors.chart.primary}><FiPackage /></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total GRNs</SummaryLabel>
            <SummaryValue>{formatNumber(summary.total)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon $color={colors.warning.main}><FiClock /></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Pending Invoice</SummaryLabel>
            <SummaryValue style={{ color: colors.warning.main }}>{formatNumber(summary.pending)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon $color={colors.success.main}><FiCheckCircle /></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Invoiced</SummaryLabel>
            <SummaryValue style={{ color: colors.success.main }}>{formatNumber(summary.invoiced)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon $color={colors.error.main}><FiFileText /></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Pending Value</SummaryLabel>
            <SummaryValue style={{ color: colors.error.main }}>{formatCurrency(summary.pendingValue)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      <TableCard>
        {loading ? (
          <Empty>Loading GRNs…</Empty>
        ) : filtered.length === 0 ? (
          <Empty>No GRNs match the selected filters.</Empty>
        ) : (
          <DataTable>
            <thead>
              <tr>
                <Th>GRN #</Th>
                <Th>Date</Th>
                <Th>Vendor</Th>
                <Th align="center">Status</Th>
                <Th align="right">Total Qty</Th>
                <Th align="right">Amount</Th>
                <Th>Linked PI(s)</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => (
                <tr key={g.id}>
                  <Td>
                    <DocLink onClick={() => handleOpenGRN(g)}>{g.docNumber}</DocLink>
                  </Td>
                  <Td>{g.docDate || '-'}</Td>
                  <Td>{g.partyName || '-'}</Td>
                  <Td align="center">
                    <Badge $color={g.isInvoiced ? colors.success.main : colors.warning.main}>
                      {g.isInvoiced ? 'Invoiced' : 'Pending'}
                    </Badge>
                  </Td>
                  <Td align="right">{formatNumber(g.totalQty || 0)}</Td>
                  <Td align="right">{formatCurrency(g.totalAmount || 0)}</Td>
                  <Td>
                    {g.linkedPIs.length === 0 ? (
                      <span style={{ color: colors.text.tertiary, fontSize: 12 }}>—</span>
                    ) : (
                      g.linkedPIs.map(pi => (
                        <PILink key={pi.id} onClick={() => handleOpenPI(pi)}>
                          {pi.docNumber} <FiExternalLink size={10} />
                        </PILink>
                      ))
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </TableCard>
    </PageLayout>
  );
};

// ── Styled ──
const FiltersCard = styled.div`
  background: white;
  padding: 18px 20px;
  border-radius: ${colors.borderRadius.md};
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  margin-bottom: 16px;
`;
const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 14px;
`;
const FilterGroup = styled.div`display:flex;flex-direction:column;gap:6px;`;
const Label = styled.label`font-size:12px;font-weight:600;color:${colors.text.secondary};`;
const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  &:focus { outline:none; border-color:${colors.primary.main}; box-shadow:0 0 0 3px ${colors.primary.main}15; }
`;
const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  background: white;
  cursor: pointer;
  &:focus { outline:none; border-color:${colors.primary.main}; box-shadow:0 0 0 3px ${colors.primary.main}15; }
`;
const ButtonGroup = styled.div`display:flex;gap:10px;justify-content:flex-end;`;
const Button = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  background: ${props => props.$primary ? colors.primary.main : 'white'};
  color: ${props => props.$primary ? 'white' : colors.text.primary};
  border: 1px solid ${props => props.$primary ? colors.primary.main : colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { background: ${props => props.$primary ? colors.primary.dark : colors.neutral[100]}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
`;
const SummaryCard = styled.div`
  background: white; padding: 14px 16px;
  border-radius: ${colors.borderRadius.md};
  display: flex; gap: 12px; align-items: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;
const SummaryIcon = styled.div`
  width: 38px; height: 38px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: ${props => `${props.$color}15`};
  color: ${props => props.$color};
  font-size: 18px;
`;
const SummaryInfo = styled.div`display:flex;flex-direction:column;`;
const SummaryLabel = styled.div`font-size:11px;color:${colors.text.secondary};text-transform:uppercase;letter-spacing:0.3px;`;
const SummaryValue = styled.div`font-size:18px;font-weight:700;color:${colors.text.primary};`;
const TableCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.md};
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  overflow: hidden;
`;
const DataTable = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`
  padding: 10px 14px; text-align: ${p => p.align || 'left'};
  font-size: 11px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.4px;
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;
const Td = styled.td`
  padding: 10px 14px; font-size: 13px; color: ${colors.text.primary};
  text-align: ${p => p.align || 'left'};
  border-bottom: 1px solid ${colors.neutral[100]};
`;
const Badge = styled.span`
  display: inline-block; padding: 3px 10px;
  border-radius: 4px; font-size: 11px; font-weight: 600;
  background: ${p => `${p.$color}15`};
  color: ${p => p.$color};
`;
const DocLink = styled.span`
  font-weight: 600; color: ${colors.primary.main}; cursor: pointer;
  &:hover { text-decoration: underline; }
`;
const PILink = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  font-weight: 600; color: ${colors.primary.main}; cursor: pointer;
  margin-right: 8px;
  &:hover { text-decoration: underline; }
`;
const Empty = styled.div`padding:40px;text-align:center;color:${colors.text.tertiary};font-size:14px;`;

export default GRNTracking;
